import {
  DestroyRef,
  Injectable,
  inject,
  computed,
  signal,
} from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { PlansStore } from './plans.store';
import { PlansApi } from './plans.api';
import { Result } from '../models/result';
import {
  SubscribePayload,
  AddSeatsPayload,
  DecreaseSeatsPayload,
  BreakdownPayload,
  SubscriptionBreakdown,
  ProductKind,
  PlanBlockReason,
  getTrialStatus,
  isNoSubscriptionError,
  isSubscriptionExpired,
  AI_PRODUCT_CODE,
  hasPlanFeature,
} from './plans.state';

/**
 * How often the trial countdown re-evaluates, so an open tab rolls into the next phase
 * on its own. The card counts whole days (hours only in its final stretch), so checking
 * every five minutes is plenty — the countdown is never more than that far behind.
 */
const TRIAL_TICK_MS = 5 * 60_000;

@Injectable({ providedIn: 'root' })
export class PlansFacade {
  readonly #store = inject(PlansStore);
  readonly #api = inject(PlansApi);

  readonly plans = this.#store.plans;
  readonly currentOrganization = this.#store.currentOrganization;
  readonly currentSubscription = this.#store.currentSubscription;
  readonly subscriptionLoaded = this.#store.subscriptionLoaded;
  /**
   * The org has no subscription and must buy one before it can use the app —
   * see {@link isNoSubscriptionError}. Read by the subscription guard and by
   * everything that presents a plan as "current".
   */
  readonly noSubscription = this.#store.noSubscription;
  readonly loading = this.#store.loading;
  readonly error = this.#store.error;

  /** Wall clock behind {@link trialStatus}; ticking it is what advances the countdown. */
  readonly #now = signal(Date.now());

  /**
   * Trial countdown for the active subscription — phase, days/hours left and elapsed
   * share — or `null` when the plan isn't a dated trial. Lives here so every view of
   * the trial (sidebar card, plan management) reads the same clock.
   */
  readonly trialStatus = computed(() =>
    getTrialStatus(this.currentSubscription(), this.#now()),
  );

  /** The trial's end date has passed — it buys nothing any more. */
  readonly trialExpired = computed(() => this.trialStatus()?.expired === true);

  /** A paid subscription the server marks expired — see {@link isSubscriptionExpired}. */
  readonly subscriptionExpired = computed(() =>
    isSubscriptionExpired(this.currentSubscription()),
  );

  /**
   * Why the org can't use the app, or `null` when it can. `no_subscription`
   * wins: it's the server's own verdict on the whole account, while the two
   * checks below read one subscription's dates and status.
   */
  readonly planBlockReason = computed<PlanBlockReason | null>(() => {
    if (this.noSubscription()) return 'no_subscription';

    const subscription = this.currentSubscription();
    if (!this.trialExpired() && !this.subscriptionExpired()) return null;
    // A dead trial is its own story even when the status also says `expired`,
    // so the paywall can name what actually ran out.
    return subscription?.isTrial ? 'trial_expired' : 'subscription_expired';
  });

  /**
   * The org must buy a plan before it can do anything — what the subscription
   * guard and the app shell both act on.
   */
  readonly requiresPlan = computed(() => this.planBlockReason() !== null);

  /**
   * The organization's plan includes SignIQ AI. Every AI feature is shown, and
   * every AI call made, only when it does; until the subscription is known it
   * reads as not included.
   */
  readonly aiEnabled = computed(() =>
    hasPlanFeature(this.currentSubscription(), AI_PRODUCT_CODE),
  );

  /**
   * Advances the countdown's clock, but only for orgs actually on a dated trial —
   * everyone else has nothing to re-render, and most orgs are in that group.
   */
  #tickTrialClock(): void {
    const sub = this.currentSubscription();
    if (!sub?.isTrial || !sub.endDate) return;
    this.#now.set(Date.now());
  }

  constructor() {
    const ticker = setInterval(() => this.#tickTrialClock(), TRIAL_TICK_MS);
    inject(DestroyRef).onDestroy(() => clearInterval(ticker));
  }

  /**
   * Total seats available: the subscription's seat count, falling back to the
   * plan's included USER_SEAT quantity. `null` when neither is known.
   */
  readonly seatsTotal = computed(() => {
    const sub = this.currentSubscription();
    if (sub?.seatCount != null) return sub.seatCount;
    const seat = sub?.plan?.features?.find(
      (f) => f.product?.productKind === ('USER_SEAT' as ProductKind),
    );
    return seat?.includedQuantity ?? null;
  });

  async loadCurrentOrganization(): Promise<void> {
    this.#store.setLoading(true);
    const result = await firstValueFrom(this.#api.getCurrentOrganization());
    if (result.ok) {
      this.#store.setCurrentOrganization(result.data);
    } else {
      this.#store.setError(result.error);
    }
    this.#store.setLoading(false);
  }

  async loadSubscription(organizationId: number): Promise<void> {
    this.#requestedSubscriptionOrgId = organizationId;
    const result = await firstValueFrom(
      this.#api.getSubscription(organizationId),
    );
    // An organization switch while this was in flight makes the answer stale —
    // writing it would show the previous organization's plan (and its paywall
    // verdict) under the new one.
    if (this.#requestedSubscriptionOrgId !== organizationId) return;
    if (result.ok) {
      this.#store.setCurrentSubscription(result.data);
      // Fresh trial dates deserve a fresh clock: without this the countdown would be
      // read against whatever time the facade was created, up to a tick stale.
      this.#now.set(Date.now());
    } else if (isNoSubscriptionError(result.error)) {
      // The org genuinely has nothing to bill against — this is what gates the app.
      this.#store.markNoSubscription();
    } else {
      // Any other failure (network, 500, ...) says nothing about whether the org
      // has a subscription, so it must not lock anyone out.
      this.#store.markSubscriptionLoaded();
    }
  }

  /**
   * Loads the subscription unless it's already known, sharing one in-flight
   * request between callers. The guard runs on every navigation into the app and
   * the sidebar asks on its own, so this keeps that to a single GET per session.
   *
   * The in-flight request is keyed by organization: after a switch, a caller
   * asking about the new one must not be handed the request still running for
   * the old one.
   */
  async ensureSubscriptionLoaded(organizationId: number): Promise<void> {
    if (this.subscriptionLoaded()) return;
    if (this.#subscriptionRequest?.organizationId !== organizationId) {
      const promise = this.loadSubscription(organizationId).finally(() => {
        if (this.#subscriptionRequest?.promise === promise) {
          this.#subscriptionRequest = null;
        }
      });
      this.#subscriptionRequest = { organizationId, promise };
    }
    await this.#subscriptionRequest.promise;
  }

  /** In-flight {@link ensureSubscriptionLoaded} request, so parallel callers share it. */
  #subscriptionRequest: { organizationId: number; promise: Promise<void> } | null =
    null;

  /** Organization of the most recent {@link loadSubscription}; older answers are dropped. */
  #requestedSubscriptionOrgId: number | null = null;

  async loadPlans(): Promise<void> {
    this.#store.setLoading(true);
    const result = await firstValueFrom(this.#api.listPlans());
    if (result.ok) {
      this.#store.setPlans(result.data);
    } else {
      this.#store.setError(result.error);
    }
    this.#store.setLoading(false);
  }

  async getBreakdown(
    organizationId: number,
    payload: BreakdownPayload,
  ): Promise<Result<SubscriptionBreakdown>> {
    return firstValueFrom(this.#api.getBreakdown(organizationId, payload));
  }

  async subscribe(
    organizationId: number,
    payload: SubscribePayload,
  ): Promise<Result<void>> {
    const result = await firstValueFrom(
      this.#api.createSubscription(organizationId, payload),
    );
    if (result.ok) {
      await this.loadSubscription(organizationId);
    } else {
      this.#store.setError(result.error);
    }
    return result;
  }

  async addSeats(
    organizationId: number,
    payload: AddSeatsPayload,
  ): Promise<Result<void>> {
    const result = await firstValueFrom(
      this.#api.addSeats(organizationId, payload),
    );
    if (result.ok) {
      await this.loadSubscription(organizationId);
    } else {
      this.#store.setError(result.error);
    }
    return result;
  }

  async decreaseSeats(
    organizationId: number,
    payload: DecreaseSeatsPayload,
  ): Promise<Result<void>> {
    const result = await firstValueFrom(
      this.#api.decreaseSeats(organizationId, payload),
    );
    if (result.ok) {
      await this.loadSubscription(organizationId);
    } else {
      this.#store.setError(result.error);
    }
    return result;
  }
}
